import { useEffect, useRef } from 'react';

import {
  getDependents,
  getDependentsWithIndexes,
  runDependentValidators,
  runDynamicDependents,
  runHiddenDependents,
} from 'helpers/candidate_application/dependent-field-processing';
import { readValueByName, upsertValueByName } from 'helpers/candidate_application/field-paths';
import { useDispatch, useSelector } from 'react-redux';

import {
  recalculateIdRequirements,
  selectCandidateApplication,
  updateCandidateSections,
  updateHiddenValues,
} from 'api/candidate-application-slice';

// Dynamic dependency processing - watch for field changes and process dependencies
export function useOnChangeDependentProcessing(
  watch,
  allValues,
  currentPagePrefixes,
  trigger,
  setError,
  clearErrors,
) {
  const dispatch = useDispatch();
  const { candidateFields } = useSelector(selectCandidateApplication);

  // Track if we're in initial processing phase to avoid triggering validation
  const isInitialProcessing = useRef(true);

  const {
    hiddenFields = {},
    fieldPathIndex = {},
    wrapperValidators = {},
    dynamicDependencyCodes = {},
    hiddenDependencyCodes = {},
    validationDependencyCodes = {},
    idRequirementsDependencyCodes = [],
  } = candidateFields || {};

  useEffect(() => {
    const subscription = watch((values, { name }) => {
      if (!name) return;

      if (name.includes('.')) {
        const parentName = name.split('.')[0];
        clearErrors(parentName);
      }

      // Skip validation during initial processing to prevent immediate validation errors
      // IMPORTANT: Don't trigger wrapper fields - they use manual setError, not RHF validation
      // Triggering them would clear manual errors
      const isWrapperField = wrapperValidators?.[name];
      if (!isInitialProcessing.current && !isWrapperField) {
        trigger(name);
      }

      const prevSnapshot = allValues ?? {};

      // freshest value first from RHF payload; fallback to previous snapshot
      const incomingFieldValue =
        readValueByName(values ?? {}, name) ?? readValueByName(prevSnapshot, name);

      // rebuild a fresh snapshot with the updated field value
      let nextSnapshot = upsertValueByName(prevSnapshot, name, incomingFieldValue);

      // Get the most recent sections from Redux store
      const currentSections = candidateFields?.sections || [];

      // Dynamic
      {
        const deps = getDependents(name, dynamicDependencyCodes);
        if (deps.length) {
          const updatedSections = runDynamicDependents(
            deps,
            currentSections,
            fieldPathIndex,
            incomingFieldValue,
            nextSnapshot,
          );
          if (updatedSections) {
            dispatch(updateCandidateSections(updatedSections));
          }
        }
      }

      // Hidden
      {
        const deps = getDependents(name, hiddenDependencyCodes);
        if (deps.length) {
          const hiddenPatch = runHiddenDependents(deps, nextSnapshot, hiddenFields);
          if (hiddenPatch) {
            dispatch(updateHiddenValues(hiddenPatch));
            Object.assign(nextSnapshot, hiddenPatch);
          }
        }
      }

      // Validators (section-local)
      {
        const deps = getDependentsWithIndexes(name, validationDependencyCodes);

        if (deps.length) {
          runDependentValidators(
            deps,
            currentPagePrefixes,
            wrapperValidators,
            nextSnapshot,
            trigger,
            setError,
            clearErrors,
          );
        }
      }

      // ID Requirements (check if changed field affects ID requirements)
      {
        if (idRequirementsDependencyCodes.includes(name)) {
          dispatch(recalculateIdRequirements({ latestFormValues: nextSnapshot }));
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [
    watch,
    allValues,
    wrapperValidators,
    validationDependencyCodes,
    dynamicDependencyCodes,
    hiddenDependencyCodes,
    idRequirementsDependencyCodes,
    currentPagePrefixes,
    clearErrors,
    setError,
    trigger,
    dispatch,
  ]);
}
